import React, { useEffect, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Loader2, AlertTriangle, Sparkles, ListChecks } from 'lucide-react';
// Phase 1 + Phase 2 System Consolidation — extracted intelligence UI components.
import {
  RiskBanner,
  RiskFactorsStrip,
  RiskSnapshotInline,
  ContextProvenanceBadge,
  DecisionCard,
  RecentSessionsStrip,
  AIAccuracyPanel,
} from '../components/intelligence';
// Phase 3 Step 1 — central state via useReducer + Context. Behaviour
// preserved byte-for-byte; this commit only relocates state ownership.
import {
  DecisionEngineProvider,
  useDecisionEngine,
  SESSION_KEY,
} from '../components/intelligence/state/DecisionEngineContext';
import { KeyboardShortcuts } from '../components/intelligence/KeyboardShortcuts';
import { InputBar } from '../components/intelligence/InputBar';
import { StickyToggle, StickyToggleHelper } from '../components/intelligence/StickyToggle';
import { AutocompleteDropdown } from '../components/intelligence/AutocompleteDropdown';
import { PlayerPickerDropdown } from '../components/intelligence/PlayerPickerDropdown';
import { SessionHeader } from '../components/intelligence/SessionHeader';
import { MessageFeed } from '../components/intelligence/MessageFeed';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All page logic lives in the inner component so it can read state from the
// context provider mounted by the outer wrapper.
const DecisionEngineInner = () => {
  const { user } = useAuth();
  const { state, dispatch, refs, scrollToBottom } = useDecisionEngine();
  const {
    input,
    history,
    loading,
    commands,
    showAutocomplete,
    autocompleteIdx,
    sessionId,
    recentRefreshKey,
    athletes,
    athletesLoaded,
    selectedPlayers,
    showPlayerPicker,
    playerPickerIdx,
    stickyContext,
  } = state;
  const { inputRef } = refs;

  // Gate: Admin/Staff only — declared before the guard so all hooks always run.
  const isAuthorized = !!user && (user.role === 'Admin' || user.role === 'Staff');

  useEffect(() => {
    if (!isAuthorized) return;
    (async () => {
      try {
        const r = await axios.get(`${API}/intelligence/commands/help`);
        dispatch({ commands: r.data || [] });
      } catch (e) {
        // Help is non-blocking — the panel still works without autocomplete.
        console.warn('[decision-engine] could not load commands', e);
      }
    })();
  }, [isAuthorized, dispatch]);

  useEffect(() => {
    scrollToBottom();
  }, [history, loading, scrollToBottom]);

  const filteredCommands = useMemo(() => {
    if (!input.startsWith('/')) return [];
    const frag = input.slice(1).toLowerCase().split(' ')[0];
    return commands.filter((c) => c.command.slice(1).toLowerCase().includes(frag));
  }, [input, commands]);

  // Lazy-load athletes list once (first time user types `@` or focuses input).
  const ensureAthletesLoaded = async () => {
    if (athletesLoaded) return;
    try {
      const r = await axios.get(`${API}/athletes`);
      dispatch({
        athletes: Array.isArray(r.data) ? r.data : [],
        athletesLoaded: true,
      });
    } catch (e) {
      // Non-blocking — selector is optional.
      console.warn('[decision-engine] could not load athletes for @-picker', e);
    }
  };

  // Extract trailing `@fragment` from input — drives the player-picker filter.
  const mentionMatch = useMemo(() => {
    const m = input.match(/@(\S*)$/);
    return m ? { full: m[0], frag: m[1] } : null;
  }, [input]);

  const filteredAthletes = useMemo(() => {
    if (!mentionMatch) return [];
    const selectedIds = new Set(selectedPlayers.map((p) => p.id));
    const q = mentionMatch.frag.toLowerCase();
    return athletes
      .filter((a) => !selectedIds.has(a.id))
      .filter((a) => !q || (a.full_name || '').toLowerCase().includes(q))
      .slice(0, 6);
  }, [mentionMatch, athletes, selectedPlayers]);

  const pickAthlete = (athlete) => {
    if (!athlete) return;
    dispatch((s) => ({
      selectedPlayers: s.selectedPlayers.some((p) => p.id === athlete.id)
        ? s.selectedPlayers
        : [...s.selectedPlayers, { id: athlete.id, full_name: athlete.full_name }],
      // Strip the trailing "@frag" from input.
      input: s.input.replace(/@(\S*)$/, '').replace(/\s{2,}$/, ' ').trimEnd() + ' ',
      showPlayerPicker: false,
      playerPickerIdx: 0,
    }));
    setTimeout(() => inputRef.current?.focus(), 30);
  };

  const removePlayer = (id) => {
    dispatch((s) => ({
      selectedPlayers: s.selectedPlayers.filter((p) => p.id !== id),
    }));
    setTimeout(() => inputRef.current?.focus(), 20);
  };

  const toggleStickyContext = () => {
    dispatch((s) => {
      const next = !s.stickyContext;
      // Turning OFF also clears chips — "no confusion about which player is
      // active", and "Must NEVER persist silently".
      const patch = { stickyContext: next };
      if (!next) patch.selectedPlayers = [];
      toast.success(next ? 'Context locked to selected player(s)' : 'Context unlocked');
      return patch;
    });
  };

  const submit = async (rawText) => {
    const text = (rawText ?? input).trim();
    if (!text || loading) return;
    const turnId = `t-${Date.now()}`;

    // Snapshot selected players for THIS turn (read once before any dispatch).
    const turnPlayerIds = selectedPlayers.map((p) => p.id);
    const turnPlayerNames = selectedPlayers.map((p) => p.full_name);
    const turnStickyEnabled = stickyContext;
    const isCommand = text.startsWith('/');

    // Open the turn + reset input + close pickers in a single dispatch.
    dispatch((s) => ({
      showAutocomplete: false,
      showPlayerPicker: false,
      history: [...s.history, { id: turnId, query: text, loading: true, ts: Date.now() }],
      input: '',
      loading: true,
      // Clear chips unless sticky is ON.
      selectedPlayers: stickyContext ? s.selectedPlayers : [],
    }));

    // Persist the per-turn context on the history item so the Provenance
    // Badge can render from message-level state (not the current UI state).
    dispatch((s) => ({
      history: s.history.map((t) =>
        t.id === turnId
          ? {
              ...t,
              context: {
                selected_player_ids: turnPlayerIds,
                selected_player_names: turnPlayerNames,
                sticky_enabled: isCommand ? false : turnStickyEnabled,
              },
            }
          : t,
      ),
    }));

    try {
      const body = isCommand
        ? { command: text, session_id: sessionId, role: user.role }
        : {
            query: text,
            role: user.role,
            context: {
              selected_player_ids: turnPlayerIds,
              session_id: sessionId,
              sticky_enabled: stickyContext,
            },
          };
      const endpoint = isCommand ? '/intelligence/command' : '/intelligence/ask';
      const r = await axios.post(`${API}${endpoint}`, body);
      // Freeze the drivers from the response as the turn's risk snapshot —
      // guarantees the inline strip reflects historical truth even after a
      // resume (where it'll be hydrated from the backend-stored value).
      const drivers = (r.data?.pre_risk_flags?.drivers) || null;
      const snapshot = drivers
        ? {
            injury: (drivers.injury || []).slice(0, 2),
            fatigue: (drivers.fatigue || []).slice(0, 2),
            load: (drivers.load || []).slice(0, 2),
          }
        : null;
      // Live evolution = snapshot is current state, so all are "active".
      const totalSnap = snapshot
        ? (snapshot.injury.length + snapshot.fatigue.length + snapshot.load.length)
        : 0;
      const evolution = totalSnap > 0 && turnPlayerIds.length > 0
        ? { active_count: totalSnap, resolved_count: 0 }
        : null;
      dispatch((s) => ({
        history: s.history.map((t) =>
          t.id === turnId
            ? { ...t, loading: false, response: r.data, risk_snapshot: snapshot, risk_evolution: evolution }
            : t,
        ),
        recentRefreshKey: s.recentRefreshKey + 1, // bump so the strip re-pulls
      }));
    } catch (e) {
      const msg =
        e.response?.data?.detail ||
        e.message ||
        'Something went wrong. Please try again.';
      dispatch((s) => ({
        history: s.history.map((t) => (t.id === turnId ? { ...t, loading: false, error: msg } : t)),
      }));
      // Restore the selected players so the coach can retry without re-@-ing.
      if (turnPlayerIds.length) {
        dispatch((s) => ({
          selectedPlayers: s.selectedPlayers.length
            ? s.selectedPlayers
            : turnPlayerIds
                .map((id) => athletes.find((a) => a.id === id))
                .filter(Boolean)
                .map((a) => ({ id: a.id, full_name: a.full_name })),
        }));
      }
      toast.error(msg);
    } finally {
      dispatch({ loading: false });
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  };

  const clearSession = () => {
    const newId = `intel-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(SESSION_KEY, newId);
    dispatch({
      sessionId: newId,
      history: [],
      input: '',
      showAutocomplete: false,
      // Clear sticky context + chips: session scope is gone.
      stickyContext: false,
      selectedPlayers: [],
    });
    toast.success('Session cleared');
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  const resumeSession = async (sid) => {
    if (!sid || sid === sessionId) return;
    try {
      const r = await axios.get(`${API}/intelligence/sessions/${sid}`);
      const turns = (r.data?.messages || []).map((m, i) => ({
        id: `resumed-${sid}-${i}`,
        query: m.query || '',
        response: m.response || null,
        loading: false,
        ts: new Date(m.timestamp || Date.now()).getTime(),
        context: m.context || null,  // { selected_player_ids, selected_player_names, sticky_enabled }
        risk_snapshot: m.risk_snapshot || null,  // { injury[], fatigue[], load[] }
        risk_evolution: m.risk_evolution || null, // { active_count, resolved_count }
      }));
      sessionStorage.setItem(SESSION_KEY, sid);
      dispatch({
        sessionId: sid,
        history: turns,
        // New session scope → reset sticky context + any leftover chips.
        stickyContext: false,
        selectedPlayers: [],
      });
      toast.success('Session resumed');
      setTimeout(() => {
        scrollToBottom();
      }, 80);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load session');
    }
  };

  const onInputKeyDown = (e) => {
    // Player-picker nav takes priority when it's open (before commands).
    if (showPlayerPicker && filteredAthletes.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        dispatch((s) => ({
          playerPickerIdx: Math.min(s.playerPickerIdx + 1, filteredAthletes.length - 1),
        }));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        dispatch((s) => ({ playerPickerIdx: Math.max(s.playerPickerIdx - 1, 0) }));
        return;
      }
      if (e.key === 'Tab' || e.key === 'Enter') {
        const picked = filteredAthletes[playerPickerIdx];
        if (picked) {
          e.preventDefault();
          pickAthlete(picked);
          return;
        }
      }
      if (e.key === 'Escape') {
        dispatch({ showPlayerPicker: false });
        return;
      }
    }
    // Remove last chip when backspacing on empty input.
    if (e.key === 'Backspace' && input === '' && selectedPlayers.length > 0) {
      e.preventDefault();
      dispatch((s) => ({ selectedPlayers: s.selectedPlayers.slice(0, -1) }));
      return;
    }
    // Autocomplete nav.
    if (showAutocomplete && filteredCommands.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        dispatch((s) => ({
          autocompleteIdx: Math.min(s.autocompleteIdx + 1, filteredCommands.length - 1),
        }));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        dispatch((s) => ({ autocompleteIdx: Math.max(s.autocompleteIdx - 1, 0) }));
        return;
      }
      if (e.key === 'Tab' || (e.key === 'Enter' && filteredCommands[autocompleteIdx])) {
        const picked = filteredCommands[autocompleteIdx];
        if (picked) {
          e.preventDefault();
          const next = picked.command + (picked.args && picked.args !== 'none' ? ' ' : '');
          dispatch({ input: next, showAutocomplete: false, autocompleteIdx: 0 });
          return;
        }
      }
      if (e.key === 'Escape') {
        dispatch({ showAutocomplete: false });
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      submit();
    }
  };

  const onInputChange = (v) => {
    dispatch({
      input: v,
      showAutocomplete: v.startsWith('/'),
      autocompleteIdx: 0,
    });
    // Player picker: triggered by a trailing `@...` token.
    const mentionActive = /@(\S*)$/.test(v);
    if (mentionActive) {
      ensureAthletesLoaded();
      dispatch({ showPlayerPicker: true, playerPickerIdx: 0 });
    } else {
      dispatch({ showPlayerPicker: false });
    }
  };

  // Auth guard — rendered AFTER all hooks to keep hook order stable.
  if (!user) return null;
  if (!isAuthorized) return <Navigate to="/dashboard" replace />;

  return (
    <div className="h-[calc(100vh-4rem)] flex flex-col bg-gray-50 dark:bg-gray-950" data-testid="decision-engine-page">
      <KeyboardShortcuts onClearSession={clearSession} />
      <SessionHeader onClear={clearSession} />

      <MessageFeed user={user} onSubmit={submit} onResumeSession={resumeSession} />

      {/* Input bar */}
      <div className="border-t bg-white dark:bg-gray-900 px-6 py-4 relative">
        <AutocompleteDropdown
          filteredCommands={filteredCommands}
          onPick={(c) => {
            const next = c.command + (c.args && c.args !== 'none' ? ' ' : '');
            dispatch({ input: next, showAutocomplete: false });
            inputRef.current?.focus();
          }}
        />

        {/* Player @-picker dropdown */}
        <PlayerPickerDropdown
          filteredAthletes={filteredAthletes}
          onPick={pickAthlete}
        />

        <div className="max-w-3xl mx-auto flex gap-2 items-center">
          <InputBar
            onInputChange={onInputChange}
            onInputKeyDown={onInputKeyDown}
            onSubmit={() => submit()}
            onRemovePlayer={removePlayer}
            onFocusInput={ensureAthletesLoaded}
          />
          <StickyToggle onToggle={toggleStickyContext} />
        </div>
        <StickyToggleHelper />
      </div>
    </div>
  );
};

// Page-level wrapper — mounts the state provider so the inner component can
// read everything via `useDecisionEngine()`. Future child-component
// extractions (Phase 3 Step 2) will consume the same context.
const DecisionEnginePage = () => (
  <DecisionEngineProvider>
    <DecisionEngineInner />
  </DecisionEngineProvider>
);

export default DecisionEnginePage;
